import * as React from 'react';

/**
 * Field aria plumbing — the WCAG 3.3.1 (Error Identification) wiring shared by
 * the whole form archetype.
 *
 * The `Field` wrapper owns a `FieldAriaContext`; `FieldDescription` and
 * `FieldError` register their DOM ids into it while rendered, and the form
 * controls (`Input`, `Textarea`, `SelectTrigger`, …) read the registry to
 * derive `aria-invalid` + `aria-describedby`. As a result, every consumer
 * that composes the archetype —
 *
 *   <Field>
 *     <FieldLabel htmlFor="qty">Quantity</FieldLabel>
 *     <Input id="qty" … />
 *     <FieldError errors={qtyErrors} />
 *   </Field>
 *
 * — gets a screen-reader-announced error association with ZERO per-call aria
 * wiring: when `FieldError` renders content, the control announces
 * `aria-invalid="true"` and `aria-describedby` pointing at the `role="alert"`
 * error element; when the field is clean, neither attribute is emitted (no
 * dangling references). Helper text (`FieldDescription`) composes into the
 * same `aria-describedby`, space-separated, never clobbered.
 *
 * Everything that can be pure IS pure (and JSX-free) so the `node --test`
 * sweep covers the decision table without a DOM; the rendered-attribute
 * contract is asserted end-to-end in
 * `tests/e2e/a11y/archetype-a11y.spec.ts`.
 */

// ─── Pure id-registry helpers ─────────────────────────────────────

/** Add an id to a registry list; identity-stable when already present. */
export function addAriaId(ids: readonly string[], id: string): readonly string[] {
  return ids.includes(id) ? ids : [...ids, id];
}

/** Remove an id from a registry list; identity-stable when absent. */
export function removeAriaId(ids: readonly string[], id: string): readonly string[] {
  return ids.includes(id) ? ids.filter((existing) => existing !== id) : ids;
}

/**
 * Compose an `aria-describedby` token list from any mix of caller-supplied
 * strings (which may already hold several space-separated ids) and registry
 * ids. Tokens are de-duplicated and order-preserving; returns `undefined`
 * when nothing remains so the attribute is omitted rather than dangling.
 */
export function composeDescribedBy(
  ...parts: Array<string | false | null | undefined>
): string | undefined {
  const tokens: string[] = [];
  for (const part of parts) {
    if (!part) continue;
    for (const token of part.split(/\s+/)) {
      if (token && !tokens.includes(token)) tokens.push(token);
    }
  }
  return tokens.length > 0 ? tokens.join(' ') : undefined;
}

// ─── Control aria resolution ──────────────────────────────────────

/** The slice of Field registry state a control needs to derive its aria. */
export interface FieldAriaSnapshot {
  /** Ids of rendered helper/description elements inside the Field. */
  descriptionIds: readonly string[];
  /** Ids of rendered error elements (only registered when they have content). */
  errorIds: readonly string[];
  /** Consumer-declared invalid state (`data-invalid` on `<Field>`). */
  declaredInvalid?: boolean;
}

/** Explicit aria the caller passed on the control itself — always wins. */
export interface ExplicitControlAria {
  ariaInvalid?: React.AriaAttributes['aria-invalid'];
  ariaDescribedBy?: string;
}

export interface ResolvedControlAria {
  'aria-invalid': React.AriaAttributes['aria-invalid'] | undefined;
  'aria-describedby': string | undefined;
}

/**
 * Derive the control's `aria-invalid` + `aria-describedby` from the Field
 * registry, merged with any explicit caller aria:
 *
 *   - explicit `aria-invalid` (including `false`) always overrides;
 *   - otherwise `aria-invalid="true"` iff an error is registered (or the
 *     Field declares `data-invalid`), and OMITTED when clean;
 *   - `aria-describedby` = caller tokens + description ids + error ids,
 *     de-duplicated (so a consumer that also hand-wired `useFieldError`
 *     does not produce duplicate tokens) and omitted when empty.
 */
export function resolveFieldControlAria(
  snapshot: FieldAriaSnapshot | undefined,
  explicit: ExplicitControlAria = {},
): ResolvedControlAria {
  const hasError = (snapshot?.errorIds.length ?? 0) > 0;
  const invalid =
    explicit.ariaInvalid ??
    (hasError || snapshot?.declaredInvalid ? (true as const) : undefined);
  return {
    'aria-invalid': invalid,
    'aria-describedby': composeDescribedBy(
      explicit.ariaDescribedBy,
      ...(snapshot?.descriptionIds ?? []),
      ...(snapshot?.errorIds ?? []),
    ),
  };
}

// ─── Flat-issue-list field errors (hand-built validation pattern) ─

/** Flat issue shape used by the hand-built `errors: Array<{ field?, message }>`
 *  validation pattern across the regulated module forms. */
export interface FieldErrorIssue {
  field?: string;
  message: string;
}

export interface UseFieldErrorResult {
  /** Stable id for the rendered `<FieldError>` — pass as its `id` and use as
   *  the control's `aria-describedby` target. */
  errorId: string;
  /** True when this field has at least one issue. */
  hasError: boolean;
  /** This field's issues, shaped for `<FieldError errors={...} />`. */
  errors: Array<{ message: string }>;
  /** Spread onto the input/control: wires `aria-invalid` + `aria-describedby`
   *  (the latter only when there is an error, so it never points at an absent
   *  node). */
  fieldProps: {
    'aria-invalid': true | undefined;
    'aria-describedby': string | undefined;
  };
}

/**
 * Pure core of `useFieldError` / `useFormArchetype().fieldError` — given the
 * flat issue list a view tracks, a field name, and a stable id prefix,
 * returns the error id plus ready-to-spread `aria-invalid` /
 * `aria-describedby` props (WCAG 3.3.1).
 */
export function resolveFieldError(
  issues: ReadonlyArray<FieldErrorIssue> | undefined,
  field: string,
  idPrefix: string,
): UseFieldErrorResult {
  const errors = (issues ?? [])
    .filter((e) => e.field === field)
    .map((e) => ({ message: e.message }));
  const hasError = errors.length > 0;
  const errorId = `${idPrefix}-${field}-error`;
  return {
    errorId,
    hasError,
    errors,
    fieldProps: {
      'aria-invalid': hasError ? (true as const) : undefined,
      'aria-describedby': hasError ? errorId : undefined,
    },
  };
}

// ─── React context + hooks ────────────────────────────────────────

export interface FieldAriaContextValue extends FieldAriaSnapshot {
  /** Register a rendered description id; returns the unregister cleanup. */
  registerDescription: (id: string) => () => void;
  /** Register a rendered error id; returns the unregister cleanup. */
  registerError: (id: string) => () => void;
}

/** Owned by `<Field>`; `null` outside a Field (controls then change nothing). */
export const FieldAriaContext = React.createContext<FieldAriaContextValue | null>(
  null,
);

/** Builds the registry a `<Field>` provides to its subtree. */
export function useFieldAriaRegistry(declaredInvalid: boolean): FieldAriaContextValue {
  const [descriptionIds, setDescriptionIds] = React.useState<readonly string[]>([]);
  const [errorIds, setErrorIds] = React.useState<readonly string[]>([]);

  // Register callbacks are identity-stable so registrants can depend on them
  // without re-running their effects when the registry contents change.
  const registerDescription = React.useCallback((id: string) => {
    setDescriptionIds((ids) => addAriaId(ids, id));
    return () => setDescriptionIds((ids) => removeAriaId(ids, id));
  }, []);
  const registerError = React.useCallback((id: string) => {
    setErrorIds((ids) => addAriaId(ids, id));
    return () => setErrorIds((ids) => removeAriaId(ids, id));
  }, []);

  return React.useMemo(
    () => ({
      descriptionIds,
      errorIds,
      declaredInvalid,
      registerDescription,
      registerError,
    }),
    [descriptionIds, errorIds, declaredInvalid, registerDescription, registerError],
  );
}

/**
 * For `FieldDescription` / `FieldError`: resolves the element's id (explicit
 * id wins; otherwise a stable generated one when inside a Field) and keeps it
 * registered in the enclosing Field's registry while `active`.
 */
export function useRegisterFieldSlot(
  slot: 'description' | 'error',
  explicitId: string | undefined,
  active: boolean,
): string | undefined {
  const ctx = React.useContext(FieldAriaContext);
  const generatedId = React.useId();
  const resolvedId = explicitId ?? (ctx ? `${generatedId}-field-${slot}` : undefined);
  const register = slot === 'description' ? ctx?.registerDescription : ctx?.registerError;

  React.useLayoutEffect(() => {
    if (!register || !resolvedId || !active) return undefined;
    return register(resolvedId);
  }, [register, resolvedId, active]);

  return resolvedId;
}

/**
 * For controls (`Input`, `Textarea`, `SelectTrigger`, …): merge the enclosing
 * Field's registry into the control's aria. Outside a Field (or with a clean
 * field) this returns the explicit values untouched, so existing consumers
 * render byte-for-byte identical markup.
 */
export function useFieldControlAria(explicit: ExplicitControlAria): ResolvedControlAria {
  const ctx = React.useContext(FieldAriaContext);
  return resolveFieldControlAria(ctx ?? undefined, explicit);
}
